/**
 * Direct Assignment Flow
 * 
 * Workflow 2: Agency already knows which caregiver to assign.
 * System validates and warns, but allows authorized override.
 */
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, UserPlus, Search, AlertTriangle, CheckCircle,
  Send, User, XCircle
} from "lucide-react";
import AppShell from "../../components/layout/AppShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function DirectAssignFlow() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedClientId = searchParams.get("client");
  
  const [step, setStep] = useState(1); // 1: Select Client, 2: Select Caregiver, 3: Review & Send
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedCaregiver, setSelectedCaregiver] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [caregiverSearch, setCaregiverSearch] = useState("");
  const [loading, setLoading] = useState(false);
  
  // Validation
  const [validationResult, setValidationResult] = useState(null);
  const [validating, setValidating] = useState(false);
  
  // Offer details
  const [offerDetails, setOfferDetails] = useState({
    isPrimary: false,
    effectiveDate: new Date().toISOString().split("T")[0],
    notes: "",
    expirationHours: "48_hours",
    overrideWarnings: false,
  });

  useEffect(() => {
    fetchClients();
    fetchEmployees();
  }, []);

  useEffect(() => {
    if (preselectedClientId && clients.length > 0) {
      const client = clients.find(c => c.id === preselectedClientId);
      if (client) {
        setSelectedClient(client);
        setStep(2);
      }
    }
  }, [preselectedClientId, clients]);

  const fetchClients = async () => {
    try {
      const res = await fetch(`${API}/api/clients?is_active=true`, {
        credentials: "include",
      });
      if (res.ok) {
        setClients(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch clients:", err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API}/api/users?role=employee`, {
        credentials: "include",
      });
      if (res.ok) {
        setEmployees(await res.json());
      }
    } catch (err) {
      console.error("Failed to fetch employees:", err);
    }
  };

  const handleSelectClient = (client) => {
    setSelectedClient(client);
    setStep(2);
    setValidationResult(null);
  };

  const handleSelectCaregiver = async (caregiver) => {
    setSelectedCaregiver(caregiver);
    setStep(3);
    
    // Run validation
    if (selectedClient) {
      await validateAssignment(selectedClient.id, caregiver.id);
    }
  };

  const validateAssignment = async (clientId, caregiverId) => {
    setValidating(true);
    try {
      const res = await fetch(
        `${API}/api/assignment-center/validate?client_id=${clientId}&caregiver_id=${caregiverId}`,
        {
          method: "POST",
          credentials: "include",
        }
      );
      
      if (res.ok) {
        const result = await res.json();
        setValidationResult(result);
      }
    } catch (err) {
      console.error("Failed to validate:", err);
    } finally {
      setValidating(false);
    }
  };

  const handleSendOffer = async () => {
    if (!selectedClient || !selectedCaregiver) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/assignment-center/direct-assign`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          client_id: selectedClient.id,
          caregiver_id: selectedCaregiver.id,
          is_primary: offerDetails.isPrimary,
          effective_date: offerDetails.effectiveDate,
          notes: offerDetails.notes,
          expiration_type: offerDetails.expirationHours,
          override_warnings: offerDetails.overrideWarnings,
          override_reason: offerDetails.overrideWarnings ? "Agency direct assignment override" : null,
        }),
      });
      
      const data = await res.json();
      
      if (res.ok && data.status === "offer_sent") {
        toast.success(`Offer sent to ${selectedCaregiver.name}`);
        navigate("/assignment-center");
      } else if (data.status === "validation_warnings") {
        // Show warnings and ask for override
        setValidationResult(data.validation);
        toast.info("Review validation warnings before proceeding");
      } else {
        toast.error(data.detail?.message || data.detail || "Failed to send offer");
      }
    } catch (err) {
      console.error("Failed to send offer:", err);
      toast.error("Failed to send offer");
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(c => {
    if (!clientSearch) return true;
    const query = clientSearch.toLowerCase();
    const name = `${c.first_name || ""} ${c.last_name || ""}`.toLowerCase();
    return name.includes(query);
  });

  const filteredEmployees = employees.filter(e => {
    if (!caregiverSearch) return true;
    const query = caregiverSearch.toLowerCase();
    return e.name?.toLowerCase().includes(query) || e.email?.toLowerCase().includes(query);
  });

  const hasWarnings = validationResult?.has_warnings;
  const hasCritical = validationResult?.has_critical;
  const canProceed = !hasCritical && (!hasWarnings || offerDetails.overrideWarnings);

  return (
    <AppShell>
      <div className="space-y-6" data-testid="direct-assign-flow">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/assignment-center")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-primary" />
              Direct Assignment
            </h1>
            <p className="text-muted-foreground">
              Assign a specific caregiver to a client
            </p>
          </div>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-2">
          {[
            { num: 1, label: "Select Client" },
            { num: 2, label: "Select Caregiver" },
            { num: 3, label: "Review & Send" },
          ].map(({ num, label }) => (
            <div key={num} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step >= num ? "bg-primary text-white" : "bg-muted text-muted-foreground"
              }`}>
                {step > num ? <CheckCircle className="w-5 h-5" /> : num}
              </div>
              <span className={`text-sm ${step >= num ? "font-medium" : "text-muted-foreground"}`}>
                {label}
              </span>
              {num < 3 && <div className="w-8 h-px bg-border" />}
            </div>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {/* Step 1: Select Client */}
          <Card className={step === 1 ? "ring-2 ring-primary" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">1</span>
                Client
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedClient ? (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">
                        {selectedClient.first_name} {selectedClient.last_name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {selectedClient.city}, {selectedClient.state}
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedClient(null); setStep(1); }}>
                    Change
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search clients..."
                      value={clientSearch}
                      onChange={(e) => setClientSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {filteredClients.map((client) => (
                      <div
                        key={client.id}
                        className="flex items-center justify-between p-2 rounded border hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleSelectClient(client)}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-muted-foreground" />
                          <span>{client.first_name} {client.last_name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Select Caregiver */}
          <Card className={step === 2 ? "ring-2 ring-primary" : ""}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">2</span>
                Caregiver
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedCaregiver ? (
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">{selectedCaregiver.name}</p>
                      <p className="text-sm text-muted-foreground">{selectedCaregiver.email}</p>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedCaregiver(null); setStep(2); setValidationResult(null); }}>
                    Change
                  </Button>
                </div>
              ) : step >= 2 ? (
                <>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search caregivers..."
                      value={caregiverSearch}
                      onChange={(e) => setCaregiverSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {filteredEmployees.map((emp) => (
                      <div
                        key={emp.id}
                        className="flex items-center justify-between p-2 rounded border hover:bg-slate-50 cursor-pointer"
                        onClick={() => handleSelectCaregiver(emp)}
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <span className="font-medium">{emp.name || emp.email}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <p className="text-muted-foreground text-sm">Select a client first</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Step 3: Review & Send */}
        {step === 3 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-sm">3</span>
                Review & Send Offer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Validation Results */}
              {validating ? (
                <div className="text-center py-4">
                  <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
                  <p className="text-sm text-muted-foreground mt-2">Validating assignment...</p>
                </div>
              ) : validationResult && (
                <div className={`rounded-lg p-4 ${
                  hasCritical ? "bg-red-50 border border-red-200" :
                  hasWarnings ? "bg-amber-50 border border-amber-200" :
                  "bg-green-50 border border-green-200"
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {hasCritical ? (
                      <>
                        <XCircle className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-700">Critical Issues Found</span>
                      </>
                    ) : hasWarnings ? (
                      <>
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        <span className="font-medium text-amber-700">Warnings Found</span>
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-700">Validation Passed</span>
                      </>
                    )}
                  </div>
                  
                  {validationResult.warnings?.length > 0 && (
                    <ul className="text-sm space-y-1 ml-7">
                      {validationResult.warnings.map((w, i) => (
                        <li key={i} className={
                          w.severity === "critical" ? "text-red-700" : "text-amber-700"
                        }>
                          • {w.message}
                          {!w.can_override && " (Cannot override)"}
                        </li>
                      ))}
                    </ul>
                  )}
                  
                  {hasWarnings && !hasCritical && (
                    <div className="flex items-center space-x-2 mt-3 pt-3 border-t border-amber-200">
                      <Checkbox
                        id="overrideWarnings"
                        checked={offerDetails.overrideWarnings}
                        onCheckedChange={(checked) =>
                          setOfferDetails(prev => ({ ...prev, overrideWarnings: checked }))
                        }
                      />
                      <Label htmlFor="overrideWarnings" className="text-amber-800">
                        I acknowledge these warnings and authorize this assignment
                      </Label>
                    </div>
                  )}
                </div>
              )}

              {/* Offer Details */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={offerDetails.effectiveDate}
                    onChange={(e) =>
                      setOfferDetails(prev => ({ ...prev, effectiveDate: e.target.value }))
                    }
                    className="mt-1"
                  />
                </div>
                
                <div>
                  <Label>Offer Expires In</Label>
                  <Select
                    value={offerDetails.expirationHours}
                    onValueChange={(value) =>
                      setOfferDetails(prev => ({ ...prev, expirationHours: value }))
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24_hours">24 Hours</SelectItem>
                      <SelectItem value="48_hours">48 Hours</SelectItem>
                      <SelectItem value="72_hours">72 Hours</SelectItem>
                      <SelectItem value="no_expiration">No Expiration</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="isPrimary"
                  checked={offerDetails.isPrimary}
                  onCheckedChange={(checked) =>
                    setOfferDetails(prev => ({ ...prev, isPrimary: checked }))
                  }
                />
                <Label htmlFor="isPrimary">Set as Primary Caregiver</Label>
              </div>
              
              <div>
                <Label>Notes (Optional)</Label>
                <Textarea
                  value={offerDetails.notes}
                  onChange={(e) =>
                    setOfferDetails(prev => ({ ...prev, notes: e.target.value }))
                  }
                  placeholder="Reason for direct assignment, special instructions..."
                  className="mt-1"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => navigate("/assignment-center")}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSendOffer}
                  disabled={loading || !canProceed}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {loading ? "Sending..." : "Send Offer"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppShell>
  );
}
